import { queueLocked, QueueLockedType } from './customer-states'
import { without } from 'lodash-es'
import moment from 'moment'

const postStates = {
  draft: 'draft',
  pending: 'pending',
  pendingAdminReview: 'pending_admin_review',
  unsampled: 'unsampled',
  new: 'new',
  viewed: 'viewed',
  declined: 'declined',
  posted: 'posted',
  edited: 'edited',
  editsDeclined: 'edits_declined',
  awaitingEditing: 'awaiting_editing',
  awaitingAgencyReview: 'awaiting_agency_review',
  pinned: 'pinned',
  withdrawn: 'withdrawn',
  sampled: 'sampled',
  archived: 'archived',
  bookmarked: 'bookmarked',
  unsubmittable: 'unsubmittable'
}

const allStates = Object.values(postStates)

const postContexts = {
  queued: [
    postStates.new,
    postStates.viewed,
    postStates.edited,
    postStates.editsDeclined,
    postStates.pinned,
    postStates.awaitingAgencyReview
  ],
  purchased: [
    postStates.posted
  ],
  purchaseable: [
    postStates.new,
    postStates.viewed,
    postStates.edited,
    postStates.editsDeclined,
    postStates.pinned,
    postStates.awaitingEditing,
    postStates.awaitingAgencyReview
  ],
  queuedAndAwaitingEditing: [
    postStates.new,
    postStates.viewed,
    postStates.edited,
    postStates.editsDeclined,
    postStates.pinned,
    postStates.awaitingEditing
  ],
  pendingReview: [
    postStates.new,
    postStates.viewed,
    postStates.edited,
    postStates.editsDeclined,
    postStates.pinned,
    postStates.pending,
    postStates.pendingAdminReview,
    postStates.awaitingAgencyReview,
  ],
  declinable: [
    postStates.viewed,
    postStates.edited,
    postStates.editsDeclined,
    postStates.awaitingEditing,
    postStates.pinned,
    postStates.awaitingAgencyReview,
  ],
  expandable: [
    postStates.new,
    postStates.viewed,
    postStates.edited,
    postStates.editsDeclined,
    postStates.awaitingEditing,
    postStates.awaitingAgencyReview,
    postStates.pinned,
    postStates.posted
  ],
  // writer post contexts
  submittable: [
    postStates.draft,
    postStates.awaitingEditing,
  ],
  complete: [
    postStates.unsubmittable,
    postStates.posted,
    postStates.declined,
    postStates.withdrawn
  ],
  erasableFromComplete: [
    postStates.declined,
    postStates.withdrawn
  ],
  editable: without(allStates, 'posted', 'declined', 'withdrawn', 'archived', 'pending_admin_review', 'unsubmittable'),
  all: allStates // Keys match their values
}

export const customerPostStates = postContexts.queued.concat(postContexts.complete)

const writerActionableContextDefaults = {
  archivable: [
    postStates.draft,
    postStates.pending,
    postStates.pendingAdminReview,
    postStates.new,
    postStates.unsampled,
    postStates.declined,
    postStates.withdrawn,
    postStates.unsubmittable
  ],
  deletable: [
    postStates.draft,
    postStates.pending,
    postStates.pendingAdminReview,
    postStates.new,
    postStates.unsampled,
    postStates.declined,
    postStates.withdrawn,
    postStates.bookmarked,
    postStates.unsubmittable
  ],
  recyclable: [
    postStates.declined,
    postStates.withdrawn,
    postStates.unsubmittable
  ],
  unclaimable: [
    postStates.draft,
  ]
}

interface PostStatesPost {
  state: string
  customerPublished?: boolean
  writerId?: number
  user?: { id: number }
  lastViewedByWlUserAt?: Date
  wlPostReview?: {
    id: number
    content: string
    action: string
  }
  lockStatus?: 'me' | 'colleague' | 'writer' | 'white_label' | 'unlocked'
}

// check state OR context
// published is a special case
function postInContext(p: PostStatesPost, contextOrState: string, isEditspace?: boolean): boolean {
  let state = p.state
  if (isEditspace) {
    if (contextOrState === 'new') {
      return !p.lastViewedByWlUserAt
    } else if (contextOrState === 'awaiting_agency_review') {
      return ['awaiting_agency_review', 'awaiting_editing'].includes(state) || p.lockStatus === 'colleague'
    } else if (contextOrState === 'queued'){
      return !['awaiting_agency_review', 'awaiting_editing'].includes(state) && ['me', 'unlocked'].includes(p.lockStatus)
    }
  }
  if (contextOrState === 'published') {
    const published = p.customerPublished
    return state === 'posted' && published
  } else if (contextOrState === 'notPublished') {
    const published = p.customerPublished
    return state === 'posted' && !published
  } else if (contextOrState === 'editable') {
    return !isLockedForEditing(p as any) && postContexts.editable.includes(state)
  } else {
    return postContexts.hasOwnProperty(contextOrState) ?
      postContexts[contextOrState].indexOf(state) >= 0 :
      state === contextOrState
  }
}

function statesForContext(contextOrState: string): string[] {
  return postContexts.hasOwnProperty(contextOrState) ?
    postContexts[contextOrState] :
    [contextOrState]
}

function isLockedForEditing(p: { user?: { id: number } }): boolean {
  return p && p.user && !!p.user.id
}

function postLocked(position: number, c: QueueLockedType): boolean {
  return queueLocked(c) && position > 2 * c.postsIncluded
}

function canRecycle(post: {
  state: string
  withdrawableAt: Date
  customer: {
    state: string
    postCreditsCount: number
    queuedPostsCount: number
  },
  id: number
  latestGroupChild: number
}): boolean {
  return parentable(post) && (writerActionableContextDefaults.recyclable.includes(post.state) || reusableFromActive(post))
}

function parentable({id, latestGroupChild}: {id: number, latestGroupChild: number}): boolean {
  return !latestGroupChild || latestGroupChild === id
}

function canArchive(post: {
  state: string
  withdrawableAt: Date
  keyword: {
    deadline: Date
  },
  customer: {
    state: string
    postCreditsCount: number
    queuedPostsCount: number
  }
}): boolean {
  return (!post.keyword.deadline && writerActionableContextDefaults.archivable.includes(post.state) || reusableFromActive(post) || postInContext(post, 'erasableFromComplete')
  )
}

function canDelete(post: {
  state: string
  withdrawableAt: Date
  keyword: {
    deadline: Date
  },
  customer: {
    state: string
    postCreditsCount: number
    queuedPostsCount: number
  }
}): boolean {
  return ['bookmarked', 'unsubmittable'].includes(post.state) || (!post.keyword.deadline && writerActionableContextDefaults.deletable.includes(post.state) || reusableFromActive(post) || postInContext(post, 'erasableFromComplete')
  )
}

function canUnclaim(post: {state: string, keyword: {deadline: Date}}): boolean {
  return !!post.keyword.deadline && writerActionableContextDefaults.unclaimable.includes(post.state)
}

export interface ReusableFromActivePost {
  state: string
  withdrawableAt: Date
  customer: {
    state: string
    postCreditsCount: number
    queuedPostsCount: number
  }
}
function reusableFromActive(post: ReusableFromActivePost): boolean {
  return post.customer && postContexts.queued.includes(post.state) && (
    (['active', 'paused'].includes(post.customer.state) && post.withdrawableAt && moment().isSameOrAfter(moment(post.withdrawableAt))) ||
    (post.customer.state === 'canceled' && (post.customer.postCreditsCount === 0 || post.customer.queuedPostsCount > post.customer.postCreditsCount)) ||
    (post.customer.state === 'expired')
  )

}

export { postContexts, postInContext, statesForContext, postLocked, canDelete, canUnclaim, canArchive, canRecycle, reusableFromActive }
